import { urlValidator } from '@abovo/shared-angular-utils';
import { ACL_TARGET_APPROVALS, useAclPermission, useEntityPermission } from '@abovo/shared-auth';
import {
   ApprovalRequest,
   ApprovalRequestAttachment,
   ApprovalRequestItem,
   ApprovalReviewer,
   Client,
   CreateApprovalRequest,
   CreateApprovalReviewer,
   Modify,
   UpdateApprovalRequest,
} from '@abovo/shared-models';
import { ClientSelectDropdownComponent, CustomDialogUxBase, DateInputComponent, SharedUiCoreModule, TextInputComponent } from '@abovo/shared-ui-components';
import { noopFn } from '@abovo/shared-utils';
import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { RequestStatusSlugs } from '../../constants/approval-request.constants';
import { ApprovalReviewersSectionComponent } from '../../views/approval-reviewers-section.component';
import { ApprovalStatusBadgeComponent } from '../../views/approval-status-badge.component';
import { ApprovalUrlPreviewComponent } from '../../views/approval-url-preview.component';

/**
 * The approval request shape the dialog accepts: an existing request to view/edit,
 * or a prefill for one that does not exist yet. Compared to {@link ApprovalRequest}:
 * there is no `id` before creation, `items` only need their entity reference,
 * `reviewers` may contain unsaved ones and attachments are absent.
 */
export type ApprovalRequestDialogInput = Modify<
   ApprovalRequest,
   {
      id?: number;
      items?: Pick<ApprovalRequestItem, 'entity_id' | 'entity_type'>[];
      reviewers?: (ApprovalReviewer | CreateApprovalReviewer)[];
      approval_request_attachments?: ApprovalRequestAttachment[];
   }
>;

export interface ApprovalRequestDialogData {
   approvalRequest: ApprovalRequestDialogInput;
   preSelectedReviewers?: CreateApprovalReviewer[];
}

type SaveOutput = CreateApprovalRequest | UpdateApprovalRequest;

const requiredReviewValidator: ValidatorFn = (control) => {
   const value: ApprovalReviewer[] = control.value;
   const hasRequiredReviewer = value.some((reviewer) => reviewer.role_type.slug === 'required');
   return hasRequiredReviewer ? null : { requiredReview: true };
};

@Component({
   selector: 'webvis-feature-approval-request-dialog',
   templateUrl: './approval-request-dialog.component.html',
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [
      TranslatePipe,
      MatDialogTitle,
      MatDialogContent,
      MatDialogActions,
      MatButton,
      ClientSelectDropdownComponent,
      TextInputComponent,
      DateInputComponent,
      ApprovalReviewersSectionComponent,
      ApprovalStatusBadgeComponent,
      ReactiveFormsModule,
      SharedUiCoreModule,
      ApprovalUrlPreviewComponent,
   ],
})
export class ApprovalRequestDialogComponent extends CustomDialogUxBase {
   data = inject<ApprovalRequestDialogData>(MAT_DIALOG_DATA);
   private formBuilder = inject(FormBuilder);
   override dialogRef = inject<MatDialogRef<ApprovalRequestDialogComponent>>(MatDialogRef);

   save = output<SaveOutput>();

   private useAclPermission = useAclPermission();
   private readonly createApprovalPermission = this.useAclPermission({
      target: ACL_TARGET_APPROVALS,
      operation: 'create',
      description: 'ApprovalRequestDialogComponent create approval request permission',
   });
   private useEntityPermission = useEntityPermission();
   readonly canEditApproval = this.useEntityPermission({
      entity: this.data.approvalRequest ?? null,
      permission: 'can_edit',
      description: 'ApprovalRequestDialogComponent edit approval request',
   });

   fg = this.buildForm();
   saving = signal(false);
   clientId = signal(this.data.approvalRequest?.client?.id);
   useFullScreen = signal(false);

   override get formGroup() {
      return this.fg;
   }

   private buildForm() {
      const noPermission = !this.getHasPermission();
      const reviewers = [...(this.data.approvalRequest?.reviewers ?? []), ...(this.data.preSelectedReviewers ?? [])];
      return this.formBuilder.group({
         title: [{ value: this.data.approvalRequest?.title, disabled: noPermission }, Validators.required],
         description: [{ value: this.data.approvalRequest?.description, disabled: noPermission }],
         deadline: [
            {
               value: this.data.approvalRequest?.deadline,
               disabled: noPermission || this.data.approvalRequest?.status?.slug === RequestStatusSlugs.pending,
            },
         ],
         metadata: [{ value: this.data.approvalRequest?.metadata, disabled: noPermission }, [Validators.required, urlValidator()]],
         // Client selection is also disabled when there is a client object in the dialog data
         client: [
            { value: this.data.approvalRequest?.client, disabled: !(this.getHasPermission() && !this.data.approvalRequest?.client?.id) },
            Validators.required,
         ],
         reviewers: [{ value: reviewers, disabled: noPermission }, requiredReviewValidator],
      });
   }

   private getHasPermission() {
      if (!this.data.approvalRequest?.id) {
         return this.createApprovalPermission();
      }
      return this.canEditApproval();
   }

   onSave() {
      this.validateForm()
         .then(() => {
            const approvalRequest = this.getSaveData();
            this.save.emit(approvalRequest);
         })
         .catch(noopFn);
   }

   private getSaveData(): SaveOutput {
      const approvalRequest = this.data.approvalRequest;
      const { title, description, deadline, metadata, client, reviewers } = this.fg.controls;
      // Creating an approval sends all form fields with a value, updating sends only the fields changed by the user
      const send = (control: AbstractControl) => (!approvalRequest.id && !!control.value) || control.dirty;
      const changedData = {
         ...(send(description) && { description: description.value }),
         ...(send(deadline) && { deadline: deadline.value }),
         ...(send(metadata) && { metadata: metadata.value }),
         ...(send(reviewers) && { reviewers: reviewers.value }),
      };

      if (approvalRequest.id) {
         return {
            id: approvalRequest.id,
            // When editing a cancelled request we set the status back to draft automatically
            ...(approvalRequest.status?.slug === RequestStatusSlugs.cancelled && { status: { slug: RequestStatusSlugs.draft } }),
            ...changedData,
            ...(send(title) && title.value && { title: title.value }),
            ...(send(client) && client.value && { client: client.value }),
         };
      }
      if (!title.value || !client.value) {
         throw new Error('A new approval request needs a title and a client');
      }
      return {
         ...changedData,
         title: title.value,
         client: client.value,
         // There is no way to enter the items at the moment, so they always come from the data object when we create a new request
         items: approvalRequest.items,
      };
   }

   onNotFound(notFound: boolean) {
      const control = this.fg.controls.metadata;
      if (notFound) {
         control.setErrors({ notFound: true });
         control.markAsTouched();
      } else {
         control.updateValueAndValidity();
      }
   }

   clientSelect(client?: Client) {
      this.clientId.set(client?.id);
   }
}
